import React, {
    FC,
    MouseEvent,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import styled from 'styled-components';

// theme
import aampeTheme from '../../../../constants/aampeTheme';

// context
import { EditorContext } from '../../../../context/EditorContext';

import useComponentTypesQuery from '../../../../hooks/component-types/useComponentTypesQuery';
import useAddLabeltoVariantMutation from '../../../../hooks/labels/useAddLabelToVariantMutation';
import useVariantCreateMutation from 'hooks/variants/useVariantCreateMutation';

import { ElementKind, VariantElement } from '../../../../models/editor.model';
import { UnlabelledLabel } from '../../../../models/label.model';

interface StyledProps {
    componentPrimaryColor: string;
}

interface VariantCreationInputProps {
    isFocused: boolean;
    setIsFocused: (value: boolean) => void;
    selectedElement?: { [key: string]: any };
    setEditingVariant: (value: boolean) => void;
    element: VariantElement;
    formulaId: number;
    setAlternateCreateError: (value: string | null) => void;
}

const VariantCreationInput: FC<
    React.PropsWithChildren<VariantCreationInputProps>
> = ({
    isFocused,
    setIsFocused,
    selectedElement,
    element,
    setEditingVariant,
    formulaId,
    setAlternateCreateError,
}) => {
    const { editorDetails, setEditorDetails } = useContext(EditorContext);

    const [inputText, setInputText] = useState('');

    const inputRef = useRef<HTMLInputElement>(null);

    const { data: componentTypes } = useComponentTypesQuery();

    const componentPrimaryColor = useMemo<string>(() => {
        return componentTypes?.map[element.variantType]?.text_color ?? '';
    }, [componentTypes, element]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            setEditingVariant(true);
        }
    }, [inputRef, setEditingVariant]);

    const onBlur = () => {
        if (isFocused) {
            setIsFocused(false);
            setInputText('');
        }
        if (
            selectedElement?.kind !== ElementKind.variant ||
            (selectedElement && selectedElement.elementId === element.id)
        ) {
            setEditingVariant(false);
        }
        setAlternateCreateError(null);
    };

    const mutation = useVariantCreateMutation(
        selectedElement?.elementId,
        formulaId,
        editorDetails.selectedLabel?.id !== UnlabelledLabel.id
            ? editorDetails.selectedLabel
            : undefined,
    );

    const addLabelToVariantMutation = useAddLabeltoVariantMutation(
        formulaId,
        selectedElement?.elementId,
    );

    const handleInputChange = (e: any) => {
        setInputText(e.target.value);
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        setAlternateCreateError(null);
        mutation.mutate(
            {
                content: inputText,
                component_id: selectedElement?.elementId,
            },
            {
                onSuccess: (res) => {
                    setEditorDetails({
                        ...editorDetails,
                        newly_created_alternate_id: res.data?.id,
                    });
                    if (
                        editorDetails.selectedLabel &&
                        editorDetails.selectedLabel.id !== UnlabelledLabel.id
                    ) {
                        addLabelToVariantMutation.mutate({
                            id: res.data?.id,
                            data: { label_id: editorDetails.selectedLabel?.id },
                        });
                    }
                },
                onError: (err: any) => {
                    // check that the error is a string. Otherwise, extract the `msg` from it.
                    if (err.detail) {
                        if (typeof err.detail === 'string') {
                            setAlternateCreateError(err.detail);
                        } else if (
                            Array.isArray(err.detail) &&
                            err.detail.length > 0 &&
                            err.detail[0].msg
                        ) {
                            setAlternateCreateError(err.detail[0].msg);
                        } else if (err.detail.msg) {
                            setAlternateCreateError(err.detail.msg);
                        } else {
                            setAlternateCreateError('Alternate creation error');
                        }
                    }
                },
            },
        );
        setInputText('');
    };

    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter') {
            handleSubmit(event);
        }
    };

    const handleInputClick = (e: MouseEvent<HTMLInputElement>) => {
        e.stopPropagation();
    };

    return (
        <VariantInput
            ref={inputRef}
            value={inputText}
            onChange={handleInputChange}
            placeholder={'Add an alternate...'}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            componentPrimaryColor={componentPrimaryColor}
            onClick={handleInputClick}
        />
    );
};

const VariantInput = styled.input.withConfig({
    shouldForwardProp: (prop) => !['variantType'].includes(prop), // Prevent forwarding 'variantType' prop
})<StyledProps>(({ color }) => ({
    padding: aampeTheme.spacing(1),
    margin: aampeTheme.spacing(0, 0.5),
    borderRadius: aampeTheme.borderRadius.default,
    fontSize: '14px',
    lineHeight: '20px',
    outline: `2px solid ${color}`,
    border: 'none',
}));

export default VariantCreationInput;
